export interface ClientJobQuote {
  id?: string;
  deletedAt?: string | null;
  updatedAt?: string;
  issuedAt?: string | null;
  createdAt?: string;
}

export interface ClientJobEnquiry {
  id: string;
  clientKey: string;
  linkedQuoteId?: string | null;
  receivedAt?: string;
}

export type ClientJobRef =
  | { kind: 'quote'; id: string; at: string }
  | { kind: 'enquiry'; id: string; at: string };

/** Live quotations for this person, plus enquiries that are not already attached to one of them. */
export function listClientJobs(
  clientKey: string,
  quotes: ClientJobQuote[],
  enquiries: ClientJobEnquiry[],
  keyForQuote: (quote: ClientJobQuote) => string,
): ClientJobRef[] {
  const liveQuotes = (Array.isArray(quotes) ? quotes : []).filter(
    (quote) => Boolean(quote && quote.id) && !quote.deletedAt && keyForQuote(quote) === clientKey,
  );
  const quoteIds = new Set(liveQuotes.map((quote) => String(quote.id)));
  const jobs: ClientJobRef[] = [
    ...liveQuotes.map((quote) => ({
      kind: 'quote' as const,
      id: String(quote.id),
      at: String(quote.updatedAt || quote.issuedAt || quote.createdAt || ''),
    })),
    ...(Array.isArray(enquiries) ? enquiries : [])
      .filter((enquiry) => enquiry
        && enquiry.clientKey === clientKey
        && (!enquiry.linkedQuoteId || !quoteIds.has(enquiry.linkedQuoteId)))
      .map((enquiry) => ({
        kind: 'enquiry' as const,
        id: enquiry.id,
        at: String(enquiry.receivedAt || ''),
      })),
  ];
  return jobs.sort((left, right) => right.at.localeCompare(left.at) || left.id.localeCompare(right.id));
}

const STAGE_RANK: Record<string, number> = {
  editing: 0, delivered: 1, booked: 2, accepted: 3, quoted: 4, enquiry: 5, closed: 6, lost: 7,
};

/** The most "live" stage across a client's jobs: an editing job outranks a closed one. */
export function leadStage<T extends { stage?: string }>(jobs: T[]): string {
  const ranked = jobs
    .map((job) => job.stage || '')
    .filter(Boolean)
    .sort((left, right) => (STAGE_RANK[left] ?? 9) - (STAGE_RANK[right] ?? 9));
  return ranked[0] || '';
}

export function summarizeClientJobs(
  jobs: Array<{ needsAction: boolean; journeyStatus: string; nextAction: string; stage?: string }>,
) {
  if (jobs.length === 0) {
    return { label: 'No jobs yet', next: '—', needsAction: false, multiple: false, stage: 'enquiry' };
  }
  if (jobs.length === 1) {
    return {
      label: jobs[0].journeyStatus,
      next: jobs[0].nextAction,
      needsAction: jobs[0].needsAction,
      multiple: false,
      stage: jobs[0].stage || '',
    };
  }
  const needing = jobs.filter((job) => job.needsAction).length;
  let next = 'Open to see each job';
  if (needing === 1) next = '1 job needs you';
  else if (needing > 1) next = `${needing} jobs need you`;
  return {
    label: `${jobs.length} jobs`,
    next,
    needsAction: needing > 0,
    multiple: true,
    stage: leadStage(jobs),
  };
}
